import type { Action, AppState } from './state';
import { initialState } from './state';

export function reducer(s: AppState, a: Action): AppState {
  switch (a.type) {
    // la sessione la dice il server: null = non raggiungibile, si torna al login
    case 'auth/info':
      return {
        ...s,
        authChecked: true,
        authed: a.info?.authenticated ?? false,
        user: a.info?.user ?? '',
        role: a.info?.role ?? 'viewer',
        display: a.info?.display ?? a.info?.user ?? '',
        avatar: a.info?.avatar ?? null,
        version: a.info?.version ?? s.version,
        ready: a.info?.ready ?? false,
      };
    case 'auth/profile':
      return {
        ...s,
        display: a.display ?? s.display,
        avatar: a.avatar === undefined ? s.avatar : a.avatar,
      };
    case 'auth/logout':
      return { ...initialState, authChecked: true, authed: false };

    case 'load/start':
      return { ...s, load: 'loading', loadError: undefined };
    case 'load/error':
      return { ...s, load: 'error', loadError: a.error };
    case 'load/done':
      return { ...s, load: 'ready', lastUpdate: new Date().toISOString(), ...a.data };

    case 'sse/status':
      return { ...s, sse: a.status, degraded: a.status === 'polling' || a.status === 'down' };
    case 'sse/degraded':
      return { ...s, degraded: a.degraded };

    case 'toast/push':
      return { ...s, toasts: [...s.toasts, a.toast].slice(-3) };
    case 'toast/close':
      return { ...s, toasts: s.toasts.filter((t) => t.id !== a.id) };

    case 'modal/open':
      // il visitatore guarda soltanto: niente nuove richieste (il server le rifiuta comunque)
      if (s.role === 'viewer' && a.modal.kind === 'new-request') return s;
      return { ...s, modal: a.modal };
    case 'modal/close':
      return { ...s, modal: null };

    case 'filters/set':
      return { ...s, filters: { ...s.filters, ...a.patch } };

    case 'lane/set':
      return { ...s, lane: a.lane };
    case 'drawer/open':
      return { ...s, drawer: a.drawer };
    case 'drawer/tab':
      return { ...s, drawer: s.drawer ? { ...s.drawer, tab: a.tab } : s.drawer };
    case 'drawer/close':
      return { ...s, drawer: null };
    case 'level2/set':
      return { ...s, level2: a.nodeId };

    case 'canvas/playing':
      return { ...s, playing: a.playing };
    case 'canvas/speed':
      return { ...s, speed: a.speed };
    case 'canvas/rails':
      return { ...s, showRails: a.show };

    case 'session/patch':
      return {
        ...s,
        sessions: s.sessions.map((x) =>
          x.id === a.id ? { ...x, ...a.patch, updatedAt: new Date().toISOString() } : x,
        ),
      };

    case 'session/add':
      // la stessa sessione può arrivare dalla plancia che l'ha creata e dallo stream
      return { ...s, sessions: [a.session, ...s.sessions.filter((x) => x.id !== a.session.id)] };

    case 'session/optimistic': {
      const found = s.sessions.find((x) => x.id === a.id);
      if (!found) return s;
      return { ...s, pendingOptimistic: { ...s.pendingOptimistic, [a.id]: found } };
    }
    case 'session/rollback': {
      const snap = s.pendingOptimistic[a.id];
      if (!snap) return s;
      const rest = { ...s.pendingOptimistic };
      delete rest[a.id];
      return {
        ...s,
        sessions: s.sessions.map((x) => (x.id === a.id ? snap : x)),
        pendingOptimistic: rest,
      };
    }
    case 'session/settle': {
      const rest = { ...s.pendingOptimistic };
      delete rest[a.id];
      return { ...s, pendingOptimistic: rest };
    }

    // riconciliazione per id: un evento SSE che arriva dopo non duplica nulla (§8)
    case 'message/add':
      if (s.messages.some((m) => m.id === a.message.id)) return s;
      return { ...s, messages: [...s.messages, a.message] };

    case 'message/settle': {
      // il messaggio vero può essere già arrivato dallo stream: allora la copia va solo tolta
      const real = a.id !== null && s.messages.some((m) => m.id === a.id);
      return {
        ...s,
        messages:
          a.id === null || real
            ? s.messages.filter((m) => m.id !== a.tempId)
            : s.messages.map((m) => (m.id === a.tempId ? { ...m, id: a.id as string } : m)),
      };
    }

    case 'trace/add':
      if (s.trace.some((t) => t.id === a.entry.id)) return s;
      return { ...s, trace: [...s.trace, a.entry] };

    case 'tasks/set':
      return { ...s, tasks: { ...s.tasks, [a.sessionId]: a.tasks } };
    case 'task/upsert': {
      const list = s.tasks[a.task.sessionId] ?? [];
      const next = [...list.filter((t) => t.id !== a.task.id), a.task].sort(
        (x, y) => x.position - y.position,
      );
      return { ...s, tasks: { ...s.tasks, [a.task.sessionId]: next } };
    }
    case 'files/set':
      return { ...s, files: { ...s.files, [a.sessionId]: a.files } };
    case 'file/change': {
      const rest = (s.files[a.sessionId] ?? []).filter((f) => f.path !== a.file.path);
      const next = a.removed
        ? rest
        : [...rest, a.file].sort((x, y) => x.path.localeCompare(y.path));
      return { ...s, files: { ...s.files, [a.sessionId]: next } };
    }

    case 'blackboard/add':
      if (s.blackboard.some((b) => b.id === a.entry.id)) return s;
      return { ...s, blackboard: [...s.blackboard, a.entry] };

    case 'claim/upsert': {
      const exists = s.claims.some((c) => c.id === a.claim.id);
      return {
        ...s,
        claims: exists
          ? s.claims.map((c) => (c.id === a.claim.id ? { ...c, ...a.claim } : c))
          : [...s.claims, a.claim],
      };
    }

    case 'runtime/patch': {
      let nodes = s.runtime.nodes;
      if (a.patch.nodesAdded) {
        const ids = new Set(nodes.map((n) => n.id));
        nodes = [...nodes, ...a.patch.nodesAdded.filter((n) => !ids.has(n.id))];
      }
      if (a.patch.nodesChanged) {
        nodes = nodes.map((n) => {
          const p = a.patch.nodesChanged?.find((c) => c.id === n.id);
          return p ? { ...n, ...p } : n;
        });
      }
      if (a.patch.nodesRemoved) {
        const gone = new Set(a.patch.nodesRemoved);
        nodes = nodes.filter((n) => !gone.has(n.id));
      }
      const rework = a.patch.rework
        ? [
            ...s.runtime.rework.filter((r) => !a.patch.rework?.some((x) => x.id === r.id)),
            ...a.patch.rework,
          ]
        : s.runtime.rework;
      return {
        ...s,
        runtime: { ...s.runtime, nodes, rework, updatedAt: new Date().toISOString() },
        lastUpdate: new Date().toISOString(),
      };
    }

    default:
      return s;
  }
}
